import React from 'react';
import { Info, ListChecks, RefreshCw } from 'lucide-react';
import { RegulationLink } from '../ui/RegulationLink';
import { ViewOnLabelLink } from '../ui/ViewOnLabelLink';
import { EvidenceLink } from '../ui/EvidenceLink';
const HSA = 'Singapore Health Supplement Guidelines';
type SgVerdict = 'Go' | 'Review' | 'No go' | 'Not applicable';
type SgClaim = {
  text: string;
  classification: 'Indication' | 'Presentation' | 'Voluntary';
  verdict: SgVerdict;
  recommendation?: string;
  evidenceImage?: string;
  reference?: string;
  ruleText?: string;
  subBadge?: string;
  extract?: {
    href: string;
    title: string;
  };
};
const orangeArtwork = 'https://cdn.magicpatterns.com/uploads/a3PoVyhVbRDEeL7bptPjtw/Screenshot_2026-07-29_at_9.27.40_PM.png';
const acceptable = 'Structural and health claims that suggest maintenance and support of good health are acceptable for health supplements.';
const claims: SgClaim[] = [{
  text: 'Supports immune function',
  classification: 'Indication',
  verdict: 'Go',
  subBadge: 'Condition met',
  reference: 'HSA Guidelines for Claims and Claims Substantiation of Health Supplements and Traditional Medicines, January 2025 — clause 2.2',
  ruleText: 'Health claims in HS should be limited to supporting or enhancing general health, or a function or biological activity of the body… Some examples are support healthy growth and development, relieve fatigue/tiredness, support/maintain joint mobility/flexibility and support/maintain immune health.',
  recommendation: "'Support/maintain immune health' is listed in the guidelines as an acceptable health claim for health supplements. The vitamin and mineral supplementation condition is met — vitamin A, vitamin C and zinc each exceed 15% of the Codex NRV per daily dose.",
  evidenceImage: orangeArtwork,
  extract: {
    href: 'https://cdn.magicpatterns.com/uploads/gxKB5rdqhFKunE8bGYXhde/SG_Permissible_Claims_TMHS.png',
    title: 'HSA claims guidelines, page 3 — clause 2.2'
  }
}, {
  text: 'Promotes collagen and connective tissue formation',
  classification: 'Indication',
  verdict: 'Go',
  recommendation: acceptable
}, {
  text: 'Supports immune function, vision, red blood cell and bone formation',
  classification: 'Indication',
  verdict: 'Go',
  recommendation: acceptable
}, {
  text: 'Supports immune and nervous system function; promotes growth and development',
  classification: 'Indication',
  verdict: 'Review',
  recommendation: "'Growth and development' may read as a paediatric claim. Confirm the target population."
}, {
  text: 'This product is not intended to diagnose, treat, cure, or prevent any disease.',
  classification: 'Presentation',
  verdict: 'Not applicable',
  recommendation: 'A United States disclaimer. Singapore has no equivalent requirement and no prohibition — it is simply meaningless here.'
}, {
  text: 'Instead of the usual synthetic fillers, we use fruit powders made with real fruit juices',
  classification: 'Presentation',
  verdict: 'Review',
  recommendation: 'Avoid over-emphasising the fruit content, to avoid confusion between food and health supplement products.'
}, {
  text: 'SATISFACTION GUARANTEED',
  classification: 'Presentation',
  verdict: 'Go'
}, {
  text: '619% DV VITAMIN C · 113% DV VITAMIN A · 133% DV ZINC',
  classification: 'Presentation',
  verdict: 'Review',
  recommendation: 'A Nutrition Information Panel is not required for a health supplement. Declare only the name and quantity of the actives.'
}, {
  text: 'Add to any of your favourite smoothies, yogurt or oatmeal',
  classification: 'Presentation',
  verdict: 'No go',
  recommendation: 'Refers to food use and should be removed from a health supplement label.'
}, {
  text: 'VEGAN · GLUTEN-FREE · NON-GMO · NO ARTIFICIAL SWEETENERS · NO ARTIFICIAL FLAVORS · NO PRESERVATIVES',
  classification: 'Voluntary',
  verdict: 'Go'
}];
export function SgClaimsTab() {
  return <div className="space-y-4" data-id="element-2335" data-mp="lu1acu62dtnn">
      <header className="flex items-start gap-3" data-id="element-2336" data-mp="lt00uu4p5f81">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#eff3ed] text-forest-green" data-id="element-2337" data-mp="gvq22nvf3r7j">
          <ListChecks className="h-5 w-5" data-id="element-2338" data-mp="un4o73kmg8it" />
        </span>
        <div data-id="element-2339" data-mp="uopfmkbn3bcg">
          <h2 className="font-[Figtree] text-2xl font-semibold text-evergreen" data-id="element-2340" data-mp="qlje1pzh28cb">Claims</h2>
          <p className="mt-1 text-sm leading-relaxed text-[#6b7d70]" data-id="element-2341" data-mp="2vp1zamz8nt4">Claims extracted from the label and assessed against the Singapore Health Supplement Guidelines.</p>
        </div>
      </header>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" data-id="element-2342" data-mp="3u4ckzjxqvyxi">
        <p className="text-sm text-[#718174]" data-id="element-2343" data-mp="bu0q960df9ie">{claims.length} claims extracted from 3 label faces · analysed 26 Jul 2026</p>
        <button type="button" className="inline-flex shrink-0 items-center gap-1.5 self-start rounded-lg border border-[#d6dfd3] bg-white px-3 py-2 text-sm font-semibold text-evergreen transition hover:bg-[#f7faf5] sm:self-auto" data-id="element-2344" data-mp="1nba8xqbodxin">
          <RefreshCw className="h-4 w-4" data-id="element-2345" data-mp="1qs6fp40qeef5" />
          Re-analyse
        </button>
      </div>

      <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-relaxed text-amber-900" data-id="element-2346" data-mp="1uwe4f176vudd">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" data-id="element-2347" data-mp="1lu4ry5sd1qxc" />
        One claim is not usable in Singapore and three need rework. Structural and health claims that suggest maintenance and support of good health are acceptable for health supplements — the issues here are food-style wording and a United States disclaimer with no local standing.
      </div>

      {claims.map(claim => <article key={claim.text} className="rounded-2xl border border-[#dce3da] bg-white p-5 shadow-sm" data-id="element-2348" data-mp="24sz91lfar3go">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between" data-id="element-2349" data-mp="2sibgafr2d1gc">
            <div className="flex flex-wrap items-center gap-3" data-id="element-2350" data-mp="2x9a0vrj34lpe">
              <h3 className="font-[Figtree] text-lg font-semibold text-evergreen" data-id="element-2351" data-mp="2xbmpgnjclubf">{claim.text}</h3>
              {claim.evidenceImage && <ViewOnLabelLink href={claim.evidenceImage} data-id="element-2352" data-mp="326fml24hdv7s" />}
            </div>
            <div className="flex shrink-0 items-center gap-2" data-id="element-2353" data-mp="324cnlslcorqx">
              <span className="rounded-md bg-[#f0f3ef] px-2 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.13em] text-[#637467]" data-id="element-2354" data-mp="2x61exl6sknaa">{claim.classification}</span>
              <VerdictBadge verdict={claim.verdict} data-id="element-2355" data-mp="2o769wcfhhj8k" />
              {claim.subBadge && <span className="rounded-full bg-forest-green/10 px-2 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-forest-green" data-id="element-2356" data-mp="2jg7pb0ngpyzi">{claim.subBadge}</span>}
            </div>
          </div>
          <div className="mt-5" data-id="element-2357" data-mp="29it9n39d5e89">
            <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-[#7a897d]" data-id="element-2358" data-mp="29foytn1e7k8m">Regulatory reference</p>
            <p className="mt-1" data-id="element-2359" data-mp="20hbxfvz2twou">
              <RegulationLink text={claim.reference ?? HSA} data-id="element-2360" data-mp="20i4ycgbctrz2" />
            </p>
            {claim.ruleText && <blockquote className="mt-3 border-l-2 border-lime-glow pl-3 text-sm italic leading-relaxed text-[#617366]" data-id="element-2361" data-mp="fyj9ujgrmm45">“{claim.ruleText}”</blockquote>}
            {claim.recommendation && <div className="mt-4" data-id="element-2362" data-mp="p093lvy4dx53">
                <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-[#7a897d]" data-id="element-2363" data-mp="tyu4lxl74kul">Recommendation</p>
                <p className="mt-1 text-sm leading-relaxed text-[#506355]" data-id="element-2364" data-mp="3teqptrymtz7m">{claim.recommendation}</p>
              </div>}
            {claim.extract && <div className="mt-4 border-t border-[#eef2ec] pt-3" data-id="element-2365" data-mp="6985fa4tq5ml">
                <EvidenceLink href={claim.extract.href} title={claim.extract.title} data-id="element-2366" data-mp="67x1997guvuy" />
              </div>}
          </div>
        </article>)}

      <div className="flex items-start gap-2 rounded-xl border border-[#dbe4d8] bg-[#f5f8f3] px-4 py-3 text-sm leading-relaxed text-[#607266]" data-id="element-2367" data-mp="39qq8l49niogo">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-[#718174]" data-id="element-2368" data-mp="3emyju6kq9xik" />
        Suggestions only — not a verification. Each claim must be confirmed and, where indicated, substantiated before use.
      </div>
    </div>;
}
function VerdictBadge({
  verdict
}: {
  verdict: SgVerdict;
}) {
  const style: Record<SgVerdict, string> = {
    Go: 'bg-forest-green/10 text-forest-green ring-forest-green/20',
    Review: 'bg-amber-100 text-amber-800 ring-amber-200',
    'No go': 'bg-red-100 text-red-800 ring-red-200',
    'Not applicable': 'bg-slate-100 text-slate-500 ring-slate-200'
  };
  const label: Record<SgVerdict, string> = {
    Go: 'Permitted',
    Review: 'Permitted with conditions',
    'No go': 'Not permitted',
    'Not applicable': 'Not applicable'
  };
  return <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset ${style[verdict]}`} data-id="element-2369" data-mp="2vscqm98l580i">{label[verdict]}</span>;
}